import React, { useState, useEffect } from 'react'
import { getDivisions, getConferencesByDivision, getSchoolsByConference } from '../../services/supabaseService'

const SportSchoolStep = ({ data, onChange, onNext, onBack }) => {
  const [divisions, setDivisions] = useState([])
  const [conferences, setConferences] = useState([])
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(true)
  
  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  const updateField = (field, value) => {
    onChange({ [field]: value })
  }

  // Sports data
  const sports = [
    'Football', 'Men\'s Basketball', 'Women\'s Basketball', 'Baseball', 'Softball',
    'Men\'s Soccer', 'Women\'s Soccer', 'Men\'s Track & Field', 'Women\'s Track & Field',
    'Men\'s Swimming & Diving', 'Women\'s Swimming & Diving', 'Men\'s Tennis', 'Women\'s Tennis',
    'Men\'s Golf', 'Women\'s Golf', 'Men\'s Cross Country', 'Women\'s Cross Country',
    'Wrestling', 'Gymnastics', 'Volleyball', 'Field Hockey', 'Lacrosse', 'Ice Hockey', 'Rowing', 'Other'
  ]

  const graduationYears = ['2025', '2026', '2027', '2028', '2029', '2030']
  
  const eligibilityOptions = [
    'Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate / 5th Year'
  ]

  // Load divisions on mount
  useEffect(() => {
    const loadDivisions = async () => {
      try {
        console.log('Loading divisions...')
        
        // Try to load from Supabase first
        try {
          const divisionsData = await getDivisions()
          console.log('Divisions loaded from Supabase:', divisionsData)
          setDivisions(divisionsData)
        } catch (supabaseError) {
          console.warn('Supabase failed, using fallback data:', supabaseError)
          // Fallback to hardcoded data if Supabase isn't set up yet
          const fallbackDivisions = [
            { id: 1, code: 'D1', name: 'NCAA Division I' },
            { id: 2, code: 'D2', name: 'NCAA Division II' },
            { id: 3, code: 'D3', name: 'NCAA Division III' },
            { id: 4, code: 'NAIA', name: 'NAIA' },
            { id: 5, code: 'JUCO', name: 'JUCO / Community College' }
          ]
          setDivisions(fallbackDivisions)
        }
        
        setLoading(false)
      } catch (error) {
        console.error('Error loading divisions:', error)
        setLoading(false)
      }
    }
    loadDivisions()
  }, [])

  // Load conferences when division changes
  useEffect(() => {
    if (data.division) {
      const loadConferences = async () => {
        try {
          const conferencesData = await getConferencesByDivision(data.division)
          setConferences(conferencesData)
          // Reset conference and school selection if current conference doesn't exist in new list
          const currentConferenceExists = conferencesData.some(c => c.name === data.conference)
          if (!currentConferenceExists) {
            updateField('conference', '')
            updateField('school', '')
            setSchools([])
          } else {
            // Load schools for existing conference
            const conference = conferencesData.find(c => c.name === data.conference)
            if (conference) {
              const schoolsData = await getSchoolsByConference(conference.id, data.division)
              setSchools(schoolsData)
              // Reset school if it doesn't exist in new list
              const currentSchoolExists = schoolsData.some(s => s.name === data.school)
              if (!currentSchoolExists) {
                updateField('school', '')
              }
            }
          }
        } catch (error) {
          console.error('Error loading conferences:', error)
        }
      }
      loadConferences()
    } else {
      // Clear dependent fields when division is cleared
      setConferences([])
      setSchools([])
      updateField('conference', '')
      updateField('school', '')
    }
  }, [data.division])

  // Load schools when conference changes
  useEffect(() => {
    if (data.conference && data.division) {
      const loadSchools = async () => {
        try {
          const conference = conferences.find(c => c.name === data.conference)
          if (conference) {
            const schoolsData = await getSchoolsByConference(conference.id, data.division)
            setSchools(schoolsData)
            // Reset school selection if it doesn't exist in new list
            const currentSchoolExists = schoolsData.some(s => s.name === data.school)
            if (!currentSchoolExists) {
              updateField('school', '')
            }
          }
        } catch (error) {
          console.error('Error loading schools:', error)
        }
      }
      loadSchools()
    } else if (!data.conference) {
      // Clear schools when conference is cleared
      setSchools([])
      updateField('school', '')
    }
  }, [data.conference, data.division, conferences])

  return (
    <div className="view active">
      <div className="panel">
        <div className="panel-head" style={{
          background: 'linear-gradient(135deg,var(--navy) 0%,var(--navy3) 100%)',
          padding: '32px 36px 28px',
          borderBottom: '3px solid var(--gold)'
        }}>
          <div className="panel-title" style={{
            fontFamily: 'var(--font-d)',
            fontSize: '26px',
            color: 'var(--white)',
            marginBottom: '6px'
          }}>
            Sport, Conference & School
          </div>
          <div className="panel-subtitle" style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.55)',
            fontWeight: '300'
          }}>
            Select your sport, then your conference and division, then your college or university.
          </div>
        </div>
        
        <div className="panel-body" style={{ padding: '36px' }}>
          <form onSubmit={handleSubmit}>
            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div className="fg form-full" style={{ gridColumn: '1/-1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Sport <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.sport}
                  onChange={(e) => onChange({ sport: e.target.value })}
                  required
                >
                  <option value="">Select your sport...</option>
                  {sports.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Division <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.division}
                  onChange={(e) => onChange({ division: e.target.value })}
                  required
                >
                  <option value="">Select division...</option>
                  {divisions.map(division => (
                    <option key={division.id} value={division.code}>{division.name}</option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Conference <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.conference}
                  onChange={(e) => onChange({ conference: e.target.value })}
                  required
                >
                  <option value="">Select conference...</option>
                  {conferences.map(conference => (
                    <option key={conference.id} value={conference.name}>{conference.name}</option>
                  ))}
                </select>
              </div>

              <div className="fg form-full" style={{ gridColumn: '1/-1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  College / University <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.school}
                  onChange={(e) => onChange({ school: e.target.value })}
                  required
                >
                  <option value="">Select school...</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.name}>{school.name}</option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Expected graduation year <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.graduationYear}
                  onChange={(e) => onChange({ graduationYear: e.target.value })}
                  required
                >
                  <option value="">Select year...</option>
                  {graduationYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Year of eligibility <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.eligibility}
                  onChange={(e) => onChange({ eligibility: e.target.value })}
                  required
                >
                  <option value="">Select...</option>
                  {eligibilityOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Jersey / athlete number
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.jerseyNumber}
                  onChange={(e) => onChange({ jerseyNumber: e.target.value })}
                  placeholder="e.g. 23"
                />
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Position / event
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.position}
                  onChange={(e) => onChange({ position: e.target.value })}
                  placeholder="e.g. Point Guard, Sprinter"
                />
              </div>
            </div>

            {/* School Athletic Department Contact */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                School athletic department contact (optional)
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Athletic department contact name
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.athleticDepartmentContact}
                  onChange={(e) => onChange({ athleticDepartmentContact: e.target.value })}
                  placeholder="Compliance officer or AD contact"
                />
              </div>
              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Athletic department email
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="email"
                  value={data.athleticDepartmentEmail}
                  onChange={(e) => onChange({ athleticDepartmentEmail: e.target.value })}
                  placeholder="compliance@university.edu"
                />
              </div>
            </div>

            <div className="btn-actions" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '28px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border)'
            }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '13px 28px',
                  borderRadius: 'var(--r)',
                  fontFamily: 'var(--font-b)',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  border: '1.5px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text2)',
                  transition: 'all 0.2s'
                }}
              >
                ← Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '13px 28px',
                  borderRadius: 'var(--r)',
                  fontFamily: 'var(--font-b)',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  border: 'none',
                  transition: 'all 0.2s',
                  background: 'var(--gold)',
                  color: 'var(--navy)',
                  boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
                }}
              >
                Continue to Identity Verification →
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SportSchoolStep
